import { Alert, Input, ThemeProvider, Typography } from '@material-tailwind/react';

import { ArrowDownCircleIcon, ArrowUturnLeftIcon, FolderIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';

import _ from 'lodash';
import { Pagination } from './Pagination';

import Datepicker from 'react-tailwindcss-datepicker';
import { DailyReportFile, DailyReportFolder, DailyReportFolderGroup, DateBounds } from '../../types';

const theme = {
	input: {
		styles: {
			base: {
				container: {
					minWidth: 'min-w-[50px]',
				},
			},
		},
	},
};

interface PaginationProps {
	activePage: number;
	setActivePage: (activePage: number) => void;
}

interface Props {
	dailyReportFolderGroups: DailyReportFolderGroup | null; //todo
	dailyReportsLoaded: boolean;
	selectedDailyReportFolderGroup: DailyReportFolder[] | null;
	setSelectedDailyReportFolderGroup: (value: DailyReportFolder[]) => void;
	dailyReportFiles: DailyReportFile[] | null;
	getDownloadUrl: (fileInfo: DailyReportFile) => Promise<string>;
	locationQuery: undefined | string;
	debouncedSetLocationQuery: (value: string) => void;
	dateBounds: DateBounds;
	setDateBounds: (value: any) => void;
	paginationProps?: PaginationProps | null;
}

const DailyReportsTable = ({
	dailyReportFolderGroups,
	selectedDailyReportFolderGroup,
	setSelectedDailyReportFolderGroup,
	dailyReportFiles,
	dailyReportsLoaded,
	getDownloadUrl,
	locationQuery,
	debouncedSetLocationQuery,
	paginationProps,
	dateBounds,
	setDateBounds,
}: Props) => {
	return selectedDailyReportFolderGroup
		? SubFolderTable({
				dailyReportFiles,
				dailyReportsLoaded,
				getDownloadUrl,
				selectedDailyReportFolderGroup,
				setSelectedDailyReportFolderGroup,
				paginationProps,
				dateBounds,
				setDateBounds,
		  })
		: ParentFolderTable({
				dailyReportFolderGroups,
				setSelectedDailyReportFolderGroup,
				dailyReportsLoaded,
				locationQuery,
				debouncedSetLocationQuery,
		  });
};

const TableHeaders = ({ headers }) => {
	return (
		<thead>
			<tr>
				{headers.map((head, index) => (
					<th
						key={head.label}
						className={`cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-4 transition-colors hover:bg-blue-gray-50 ${
							head.width || ''
						}`}
						colSpan={head.colSpan || 1}
					>
						{!head.customComponent ? (
							<div className="flex items-center justify-between gap-2 font-normal leading-none">
								<div className="w-full flex gap-3 opacity-70">
									{head.setter ? (
										<div className="w-full gap-6">
											<Input
												label={head.label}
												crossOrigin=""
												variant="standard"
												size="md"
												type={head.inputType}
												onChange={(event) => head.setter(event.target.value)}
											/>
										</div>
									) : (
										<Typography variant="small" color="blue-gray" className="font-[420]">
											{head.label}
										</Typography>
									)}
								</div>
							</div>
						) : (
							head.customComponent()
						)}
					</th>
				))}
			</tr>
		</thead>
	);
};

const ParentTableRows = ({ rows, dailyReportsLoaded, headers, setSelectedDailyReportFolderGroup }) => {
	return (
		<tbody>
			{!dailyReportsLoaded && rows.length === 0 && (
				<tr className="border-b-0">
					<td colSpan={headers.map((h) => h.colSpan || 1).reduce((a, b) => a + b)} className="p-8 py-4">
						<Alert color="orange" variant="ghost">
							Loading...
						</Alert>
					</td>
				</tr>
			)}
			{rows.map((folders, index) => {
				const { folderName, folderId, producerLocationKeyName } = folders[0];

				const isLast = index === rows.length - 1;
				const classes = isLast ? 'flex items-start p-4' : 'flex items-start p-4 border-b border-blue-gray-50';

				return (
					<tr
						key={folderId + `${index}`}
						className={
							dailyReportsLoaded ? 'cursor-pointer hover:bg-gray-100' : 'pointer-events-none bg-gray-100'
						}
						onClick={() => {
							return setSelectedDailyReportFolderGroup(folders);
						}}
					>
						<td className={classes}>
							<FolderIcon className="h-5 w-5 mr-3" />
							<Typography variant="small" color="blue-gray" className="font-normal">
								{producerLocationKeyName}
							</Typography>
						</td>
					</tr>
				);
			})}
		</tbody>
	);
};

const ParentFolderTable = ({
	dailyReportFolderGroups,
	setSelectedDailyReportFolderGroup,
	dailyReportsLoaded,
	debouncedSetLocationQuery,
	locationQuery,
}) => {
	const headers = [
		{
			label: 'Location',
			fieldName: 'folderName',
			rowValueName: 'folderName',
			inputType: 'text',
			setter: debouncedSetLocationQuery,
		},
	];

	const selectedRows = _.chain(dailyReportFolderGroups)
		.pickBy((folders, producerLocationKeyName) => {
			if (!locationQuery) return true;
			const locationRegex = new RegExp(_.escapeRegExp(locationQuery), 'i');
			return locationRegex.test(producerLocationKeyName);
		})
		.values()
		.value();

	return (
		<ThemeProvider value={theme}>
			<div className="flex justify-between items-center p-3 h-20">
				<h5 className="text-xl font-semibold">Daily Reports</h5>
			</div>
			<div className="flex-col max-h-[calc(100vh-10rem)] items-center">
				<table className="border mt-4 w-full text-left">
					<TableHeaders headers={headers} />
					<ParentTableRows
						rows={selectedRows}
						dailyReportsLoaded={dailyReportsLoaded}
						headers={headers}
						setSelectedDailyReportFolderGroup={setSelectedDailyReportFolderGroup}
					/>
				</table>
			</div>
		</ThemeProvider>
	);
};

const SubFolderTable = ({
	dailyReportFiles,
	dailyReportsLoaded,
	getDownloadUrl,
	selectedDailyReportFolderGroup,
	setSelectedDailyReportFolderGroup,
	paginationProps,
	dateBounds,
	setDateBounds,
}) => {
	const headers = [
		{
			label: 'Date',
			fieldName: 'fileName',
			rowValueName: 'fileName',
			customElements,
			customComponent: () => (
				<div className="w-[100%] flex items-center justify-left gap-4">
					<div onClick={() => setSelectedDailyReportFolderGroup(null)}>
						<ArrowUturnLeftIcon className="h-5 w-5" />
					</div>
					<div>
						<Typography variant="small" color="blue-gray" className="font-[420]">
							{selectedDailyReportFolderGroup[0].producerLocationKeyName}
						</Typography>
					</div>
					<div className="w-full">
						<Datepicker
							value={dateBounds}
							onChange={(newDateBounds) => setDateBounds(newDateBounds)}
							placeholder="Enter date range"
							useRange={true}
						/>
					</div>
				</div>
			),
		},
	];

	return (
		<ThemeProvider value={theme}>
			<div className="flex justify-between items-center p-3 h-20">
				<h5 className="text-xl font-semibold">Daily Reports</h5>
			</div>
			<div className="flex-col max-h-[calc(100vh-10rem)] items-center">
				<table className="border mt-4 w-full text-left">
					<TableHeaders headers={headers} />
					<SubFolderTableRows
						rows={dailyReportFiles}
						dailyReportsLoaded={dailyReportsLoaded}
						headers={headers}
						getDownloadUrl={getDownloadUrl}
					/>
				</table>
				{paginationProps && (
					<Pagination
						activePage={paginationProps.activePage}
						setActivePage={paginationProps.setActivePage}
						onNext={paginationProps.onNext}
						prevDisabled={true}
					/>
				)}
			</div>
		</ThemeProvider>
	);
};

const SpinningDownload = () => {
	return (
		<svg
			className="animate-spin mr-3 h-5 w-5 text-white"
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
		>
			<circle className="opacity-25" cx="12" cy="12" r="10" stroke="blue" stroke-width="4"></circle>
			<path
				className="opacity-75"
				fill="blue"
				d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
			></path>
		</svg>
	);
};

const SubFolderTableRows = ({ rows, dailyReportsLoaded, headers, getDownloadUrl }) => {
	const [downloadInProgressIndex, setDownloadInProgressIndex] = useState(null);

	return (
		<tbody>
			{!dailyReportsLoaded && rows.length === 0 && (
				<tr className="border-b-0">
					<td colSpan={headers.map((h) => h.colSpan || 1).reduce((a, b) => a + b)} className="p-8 py-4">
						<Alert color="orange" variant="ghost">
							Loading...
						</Alert>
					</td>
				</tr>
			)}
			{rows.map((file, index) => {
				const { fileName, fileId, parentFolderId, parentFolderName } = file;

				const isLast = index === rows.length - 1;
				const classes = isLast
					? 'flex items-start p-4 gap-2'
					: 'flex items-start p-4 border-b border-blue-gray-50 gap-2';

				return (
					<tr
						key={fileName + `${index}`}
						className={
							dailyReportsLoaded ? 'cursor-pointer hover:bg-gray-100' : 'pointer-events-none bg-gray-100'
						}
						onClick={() => {
							setDownloadInProgressIndex(index);
							return getDownloadUrl({ fileId, fileName, parentFolderId, parentFolderName }).then(
								async (url) => {
									setDownloadInProgressIndex(null);
									window.open(url);
								}
							);
						}}
					>
						<td className={classes}>
							<Typography variant="small" color="blue-gray" className="font-normal w-max">
								{fileName}
							</Typography>
							{downloadInProgressIndex && index === downloadInProgressIndex ? (
								<SpinningDownload />
							) : (
								<ArrowDownCircleIcon className="h-5 w-5 mr-3" />
							)}
						</td>
					</tr>
				);
			})}
		</tbody>
	);
};

export default DailyReportsTable;
